import { Close, Search } from '@mui/icons-material';
import { Box, Checkbox, IconButton, InputAdornment, TextField, Tooltip } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import {
    Actions,
    Form,
    ModalNotification,
    PrimaryButton,
    SecondaryButton,
    Section,
} from 'components/common/Modal';
import { formAreaSx } from 'views/Dashboards-B2B/components/eligibility/Eligibility.styled';
import { InfoIcon } from 'views/Dashboards-B2B/components/Table.styled';
import * as S from './AddDocumentPopoverMenu.styled';

import api from 'api';
import type { DocumentTemplateSearchHit } from 'api/rest/all_documents';
import type { CreateChecklistDocument, CreateChecklistResponse } from 'api/rest/document_checklist';
import useEnabledCountries from 'hooks/useEnabledCountries';
import { chosenCountryName } from 'utils/countries';
import { loadDocs } from 'redux/application/documents/documents-middleware';
import { AppDispatch, RootState } from 'redux/store';

const SEARCH_DEBOUNCE_MS = 300;

const isAlreadyOnCase = (item: DocumentTemplateSearchHit): boolean => item.already_exists === true;

const getKey = (item: DocumentTemplateSearchHit): string => `t:${item.id}`;

const AddAllDocumentsForm = ({ closeMenu }: { closeMenu: () => void }) => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { entity: activeApp } = useSelector((state: RootState) => state.application);
    const dispatch = useDispatch<AppDispatch>();
    const { t } = useTranslation();

    const appId = activeApp?.id;
    const { codes } = useEnabledCountries();
    const country = useMemo(
        () => chosenCountryName(codes, activeApp?.country) ?? '',
        [activeApp?.country, codes]
    );

    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [documents, setDocuments] = useState<DocumentTemplateSearchHit[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [selectedItems, setSelectedItems] = useState<DocumentTemplateSearchHit[]>([]);

    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState<string | null>(null);
    const [result, setResult] = useState<CreateChecklistResponse | null>(null);

    useEffect(() => {
        const trimmed = query.trim();
        if (!trimmed) {
            setDebouncedQuery('');
            return;
        }
        const handle = window.setTimeout(() => setDebouncedQuery(trimmed), SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(handle);
    }, [query]);

    useEffect(() => {
        if (!debouncedQuery) {
            setDocuments([]);
            setLoadError(null);
            setLoading(false);
            return;
        }

        if (!appId) {
            setDocuments([]);
            setLoadError(
                t('allDocumentsLoadError', {
                    lng: language,
                    defaultValue: 'We couldn’t load documents. Please try again.',
                })
            );
            return;
        }

        if (!country) {
            setDocuments([]);
            setLoadError(
                t('allDocumentsMissingCountry', {
                    lng: language,
                    defaultValue:
                        'This case has no country, so the document catalog cannot be searched.',
                })
            );
            return;
        }

        let cancelled = false;
        const loadDocuments = async () => {
            setLoading(true);
            setLoadError(null);
            try {
                const resp = await api.allDocuments.search({
                    country,
                    q: debouncedQuery,
                    applicationId: appId,
                });
                if (cancelled) return;
                setDocuments(Array.isArray(resp.data) ? resp.data : []);
            } catch (err) {
                if (!cancelled) {
                    setDocuments([]);
                    setLoadError(
                        t('allDocumentsLoadError', {
                            lng: language,
                            defaultValue: 'We couldn’t load documents. Please try again.',
                        })
                    );
                }
                console.error(err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        loadDocuments();
        return () => {
            cancelled = true;
        };
    }, [appId, country, debouncedQuery, language, t]);

    const selectedKeys = useMemo(() => new Set(selectedItems.map(getKey)), [selectedItems]);

    const visibleDocuments = useMemo(
        () => (query.trim() ? documents : selectedItems),
        [documents, query, selectedItems]
    );
    const selectableItems = useMemo(
        () => visibleDocuments.filter((item) => !isAlreadyOnCase(item)),
        [visibleDocuments]
    );
    const selectedVisibleCount = selectableItems.filter((item) =>
        selectedKeys.has(getKey(item))
    ).length;
    const allSelected =
        selectableItems.length > 0 && selectedVisibleCount === selectableItems.length;

    const toggleDocument = (item: DocumentTemplateSearchHit) => {
        if (isAlreadyOnCase(item)) return;
        const key = getKey(item);
        setSelectedItems((prev) =>
            prev.some((selected) => getKey(selected) === key)
                ? prev.filter((selected) => getKey(selected) !== key)
                : [...prev, item]
        );
    };

    const toggleSelectAll = () => {
        if (allSelected) {
            const visibleKeys = new Set(selectableItems.map(getKey));
            setSelectedItems((prev) => prev.filter((item) => !visibleKeys.has(getKey(item))));
            return;
        }

        setSelectedItems((prev) => {
            const keys = new Set(prev.map(getKey));
            return [...prev, ...selectableItems.filter((item) => !keys.has(getKey(item)))];
        });
    };

    const canCreate = !!appId && selectedItems.length > 0 && !creating;

    const handleCreateDocuments = async () => {
        if (!canCreate || !appId) return;
        const documents: CreateChecklistDocument[] = selectedItems
            .filter((item) => !isAlreadyOnCase(item))
            .map((item) => ({ document_template_id: item.id }));

        setCreating(true);
        setCreateError(null);
        try {
            const resp = await api.documentChecklist.post(appId, { documents });
            dispatch(loadDocs(appId.toString()));
            setResult(resp.data);
        } catch (err) {
            console.error(err);
            setCreateError(
                t('checklistCreateError', {
                    lng: language,
                    defaultValue:
                        'Something went wrong creating these documents. Please try again.',
                })
            );
        } finally {
            setCreating(false);
        }
    };

    if (result) {
        const message =
            result.created_count > 0
                ? t('documentsCreatedCount', {
                      lng: language,
                      count: result.created_count,
                      defaultValue: `Added ${result.created_count} document(s) to this case.`,
                  })
                : t('documentsAlreadyOnFile', {
                      lng: language,
                      defaultValue: 'All selected documents were already on file.',
                  });
        return (
            <Form
                style={{ marginTop: '16px' }}
                onSubmit={(event) => {
                    event.preventDefault();
                    closeMenu();
                }}
            >
                <Section>
                    <ModalNotification
                        variant="success"
                        title={t('documentsCreated', {
                            lng: language,
                            defaultValue: 'Documents added',
                        })}
                        message={
                            <>
                                {message}
                                {result.created_document_names.length > 0 && (
                                    <S.CreatedList>
                                        {result.created_document_names.map((name) => (
                                            <li key={name}>{name}</li>
                                        ))}
                                    </S.CreatedList>
                                )}
                            </>
                        }
                    />
                </Section>
                <Actions>
                    <PrimaryButton type="submit">
                        {t('done', { lng: language, defaultValue: 'Done' })}
                    </PrimaryButton>
                </Actions>
            </Form>
        );
    }

    const showTypeaheadHint = !query.trim() && selectedItems.length === 0;

    return (
        <Form
            style={{ marginTop: '16px' }}
            onSubmit={(event) => {
                event.preventDefault();
                handleCreateDocuments();
            }}
        >
            <Section>
                <Box sx={formAreaSx}>
                    <TextField
                        fullWidth
                        size="small"
                        autoFocus
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder={t('searchDocuments', {
                            lng: language,
                            defaultValue: 'Search documents',
                        })}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search fontSize="small" sx={{ color: '#6d41d8' }} />
                                    </InputAdornment>
                                ),
                                ...(query
                                    ? {
                                          endAdornment: (
                                              <InputAdornment position="end">
                                                  <IconButton
                                                      size="small"
                                                      edge="end"
                                                      onClick={() => setQuery('')}
                                                      aria-label={t('clearSearch', {
                                                          lng: language,
                                                          defaultValue: 'Clear search',
                                                      })}
                                                      sx={{ color: 'text.secondary' }}
                                                  >
                                                      <Close sx={{ fontSize: 16 }} />
                                                  </IconButton>
                                              </InputAdornment>
                                          ),
                                      }
                                    : {}),
                            },
                        }}
                    />
                </Box>
            </Section>

            <S.ChecklistCard>
                <S.ChecklistHeader>
                    <span>
                        <S.ChecklistTitle>
                            {t('documents', { lng: language, defaultValue: 'Documents' })}
                        </S.ChecklistTitle>
                        {selectedItems.length > 0 && (
                            <S.ChecklistCount>{selectedItems.length}</S.ChecklistCount>
                        )}
                    </span>
                    <S.SelectAllButton
                        type="button"
                        disabled={loading || selectableItems.length === 0}
                        onClick={toggleSelectAll}
                    >
                        {allSelected
                            ? t('deselectAll', {
                                  lng: language,
                                  defaultValue: 'Deselect all',
                              })
                            : t('selectAll', { lng: language, defaultValue: 'Select all' })}
                    </S.SelectAllButton>
                </S.ChecklistHeader>

                {loading && documents.length === 0 ? (
                    <S.ChecklistMessage>
                        {t('loadingDocuments', {
                            lng: language,
                            defaultValue: 'Loading documents…',
                        })}
                    </S.ChecklistMessage>
                ) : loadError ? (
                    <S.ChecklistMessage>{loadError}</S.ChecklistMessage>
                ) : showTypeaheadHint ? (
                    <S.ChecklistMessage>
                        {t('searchDocumentsHint', {
                            lng: language,
                            defaultValue: 'Type a name to search the document catalog.',
                        })}
                    </S.ChecklistMessage>
                ) : visibleDocuments.length === 0 ? (
                    <S.ChecklistMessage>
                        {t('noMatchingDocuments', {
                            lng: language,
                            defaultValue: 'No documents match your search.',
                        })}
                    </S.ChecklistMessage>
                ) : (
                    <S.ChecklistList>
                        {visibleDocuments.map((item) => {
                            const key = getKey(item);
                            const alreadyOnCase = isAlreadyOnCase(item);
                            const checked = alreadyOnCase || selectedKeys.has(key);
                            return (
                                <S.ChecklistRow key={key}>
                                    <Checkbox
                                        size="small"
                                        checked={checked}
                                        disabled={alreadyOnCase}
                                        onChange={() => toggleDocument(item)}
                                        sx={{
                                            color: 'rgba(131, 83, 226, 0.5)',
                                            '&.Mui-checked': { color: '#8353e2' },
                                        }}
                                        slotProps={{ input: { 'aria-label': item.name } }}
                                    />
                                    <S.DocNameWrap
                                        disabled={alreadyOnCase}
                                        onClick={() => toggleDocument(item)}
                                    >
                                        <S.DocName muted={alreadyOnCase}>{item.name}</S.DocName>
                                        {item.is_form && (
                                            <S.FormBadge>
                                                {t('form', {
                                                    lng: language,
                                                    defaultValue: 'Form',
                                                })}
                                            </S.FormBadge>
                                        )}
                                        {alreadyOnCase && (
                                            <S.AddedBadge>
                                                {t('added', {
                                                    lng: language,
                                                    defaultValue: 'Added',
                                                })}
                                            </S.AddedBadge>
                                        )}
                                    </S.DocNameWrap>
                                    <Tooltip arrow title={item.description || ''} placement="top">
                                        <S.InfoWrap>
                                            <InfoIcon open={false} />
                                        </S.InfoWrap>
                                    </Tooltip>
                                </S.ChecklistRow>
                            );
                        })}
                    </S.ChecklistList>
                )}
            </S.ChecklistCard>

            {createError && (
                <ModalNotification
                    variant="error"
                    title={t('errorTitle', { lng: language, defaultValue: 'Oops!' })}
                    message={createError}
                />
            )}

            <Actions>
                <SecondaryButton type="button" onClick={closeMenu}>
                    {t('cancel', { lng: language, defaultValue: 'Cancel' })}
                </SecondaryButton>
                <PrimaryButton type="submit" disabled={!canCreate}>
                    {creating
                        ? t('creating', { lng: language, defaultValue: 'Creating…' })
                        : t('createChecklist', {
                              lng: language,
                              defaultValue: 'Create documents',
                          })}
                </PrimaryButton>
            </Actions>
        </Form>
    );
};

export default AddAllDocumentsForm;
